import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hapticFeedback } from '@telegram-apps/sdk-react';
import { api } from '@/lib/api';
import { countdownSeconds, gameStatusLine } from '@/lib/bingo';
import { etbInt } from '@/lib/money';
import { serverNow } from '@/lib/serverClock';
import { ConnectedBar } from '@/components/ConnectedBar';
import { GamePods } from '@/components/GamePods';
import { JackpotBar } from '@/components/JackpotBar';
import type { GameDetail, Me, Ticket } from '@/lib/types';

interface BoardPayload {
    board_size: number;
    taken: number[];
    mine?: number[];
}

/**
 * Walliya cartela board:
 * 1. Tap free number → yellow
 * 2. Select Number → buy (shows Confirming…) → stays yellow as yours; others stay red
 * 3. Deselect → clear yellow pending (not bought yet)
 * 4. Start Game → enter live/waiting hall with your cartelas
 */
export function TicketPurchase() {
    const { gameId } = useParams<{ gameId: string }>();
    const numericGameId = gameId ? Number(gameId) : null;
    const navigate = useNavigate();

    const [me, setMe] = useState<Me | null>(null);
    const [game, setGame] = useState<GameDetail | null>(null);
    const [boardSize, setBoardSize] = useState(0);
    const [taken, setTaken] = useState<Set<number>>(new Set());
    const [mine, setMine] = useState<Set<number>>(new Set());
    const [picked, setPicked] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [ready, setReady] = useState(false);
    const [now, setNow] = useState(() => serverNow());

    useEffect(() => {
        const id = window.setInterval(() => setNow(serverNow()), 250);
        return () => window.clearInterval(id);
    }, []);

    const load = useCallback(async (): Promise<void> => {
        if (numericGameId === null) return;
        const [meRes, gameRes, boardRes] = await Promise.all([
            api.get<{ data: Me }>('/me'),
            api.get<{ data: GameDetail }>(`/games/${numericGameId}`),
            api.get<{ data: BoardPayload }>(`/games/${numericGameId}/board`),
        ]);
        setMe(meRes.data.data);
        setGame(gameRes.data.data);
        setBoardSize(boardRes.data.data.board_size);
        const nextTaken = new Set(boardRes.data.data.taken);
        const nextMine = new Set(boardRes.data.data.mine ?? []);
        setTaken(nextTaken);
        setMine(nextMine);
        setReady(true);

        const g = gameRes.data.data;
        const secs = countdownSeconds(g.countdown_ends_at, serverNow());
        const countdownDone = secs !== null && secs <= 0;
        // Enter hall when draws start, or when countdown finished on server clock.
        if (
            nextMine.size > 0 &&
            (g.status === 'active' || g.status === 'paused' || (g.status === 'open' && countdownDone))
        ) {
            navigate(`/games/${gameId}`, { replace: true });
        }
    }, [numericGameId, gameId, navigate]);

    useEffect(() => {
        let cancelled = false;
        load().catch((e) => {
            if (!cancelled) setError(extractMessage(e));
        });
        // Poll faster while countdown is ending so maybeStart runs promptly.
        const id = window.setInterval(() => {
            void load().catch(() => undefined);
        }, 1000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [load]);

    // Local clock: leave the board when countdown hits 0 and you already have cartelas.
    useEffect(() => {
        if (!game || mine.size === 0 || !gameId) return;
        if (game.status === 'active' || game.status === 'paused') {
            navigate(`/games/${gameId}`, { replace: true });
            return;
        }
        const secs = countdownSeconds(game.countdown_ends_at, now);
        if (game.status === 'open' && secs !== null && secs <= 0) {
            navigate(`/games/${gameId}`, { replace: true });
        }
    }, [game, mine.size, now, gameId, navigate]);

    const cells = useMemo(() => Array.from({ length: boardSize }, (_, i) => i + 1), [boardSize]);
    const salesOpen = game?.status === 'open';
    const cap =
        game?.jackpot_target ??
        (game?.max_tickets
            ? game.max_tickets * (game?.stake_amount ?? 0)
            : Math.max(game?.prize_pool ?? 0, (game?.stake_amount ?? 0) * 10));

    const highlight = (n: number): void => {
        if (busy) return;
        if (taken.has(n) && !mine.has(n)) {
            setError('This number is already taken. Please select another number.');
            return;
        }
        if (mine.has(n)) {
            setPicked(n);
            setError(null);
            return;
        }
        setPicked(n);
        setError(null);
        try {
            hapticFeedback.selectionChanged();
        } catch {
            // ignore
        }
    };

    /** Walliya: Select Number = buy the yellow pick. */
    const selectNumber = async (): Promise<void> => {
        if (busy || numericGameId === null) return;

        // Pending yellow → Deselect
        if (picked !== null && !mine.has(picked) && !salesOpen) {
            setError('Sales are closed for this table.');
            return;
        }

        if (picked === null) {
            setError('Tap a free number first, then press Select Number.');
            return;
        }

        // Already yours — treat as Deselect highlight
        if (mine.has(picked)) {
            setPicked(null);
            return;
        }

        if (!salesOpen) {
            setError('Sales are closed for this table.');
            return;
        }

        setBusy(true);
        setError(null);
        try {
            await api.post<{ data: Ticket }>(`/games/${numericGameId}/tickets`, { serial: picked });
            try {
                hapticFeedback.notificationOccurred('success');
            } catch {
                // ignore
            }
            setTaken((prev) => new Set(prev).add(picked));
            setMine((prev) => new Set(prev).add(picked));
            setPicked(null);
            const statusRes = await api.get<{ data: GameDetail }>(`/games/${numericGameId}`);
            setGame(statusRes.data.data);
            await load();
        } catch (e) {
            const msg = extractMessage(e);
            setError(msg.includes('taken') || msg.includes('already')
                ? 'This number is already taken. Please select another number.'
                : msg);
            setPicked(null);
            try {
                hapticFeedback.notificationOccurred('error');
            } catch {
                // ignore
            }
            await load().catch(() => undefined);
        } finally {
            setBusy(false);
        }
    };

    const deselect = (): void => {
        setPicked(null);
        setError(null);
    };

    const startGame = (): void => {
        if (mine.size === 0) {
            setError('Select and buy at least one cartela first.');
            return;
        }
        navigate(`/games/${gameId}`);
    };

    const statusLine = game ? gameStatusLine(game.status, game.countdown_ends_at, now) : '';
    const hasPending = picked !== null && !mine.has(picked);
    const middleBusy = busy;
    const middleDeselect = !busy && picked !== null && !hasPending;
    const middleSelect = !busy && hasPending;
    const middleDisabled = busy || picked === null;

    let middleLabel = 'Select Number';
    if (middleBusy) middleLabel = 'Confirming…';
    else if (middleDeselect) middleLabel = 'Deselect';
    else if (middleSelect) middleLabel = 'Select Number';

    return (
        <div className="lobby lobby-board w-screen w-purchase">
            <ConnectedBar />
            <GamePods
                walletBalance={me?.wallet.balance ?? null}
                activeGames={game?.status === 'active' || game?.status === 'paused' ? 1 : 0}
                stake={game?.stake_amount ?? null}
            />

            {game && (
                <JackpotBar
                    stakeLabel={`${etbInt(game.stake_amount)} ETB`}
                    prizePool={game.prize_pool}
                    cap={cap}
                />
            )}

            {statusLine && <p className="w-status-line">{statusLine}</p>}

            {error && (
                <div className="w-taken-toast" role="alert">
                    <span className="w-taken-ico">!</span>
                    <span>{error}</span>
                    <button type="button" className="w-toast-x" onClick={() => setError(null)} aria-label="Dismiss">
                        ×
                    </button>
                </div>
            )}

            {!ready && !error && <div className="lobby-empty">Loading board…</div>}

            {ready && (
                <div className="board-grid w-board-grid">
                    {cells.map((n) => {
                        const isMine = mine.has(n);
                        const isTaken = taken.has(n) && !isMine;
                        const isPicked = picked === n && !isMine;
                        return (
                            <button
                                key={n}
                                type="button"
                                className={[
                                    'board-cell',
                                    isMine ? 'mine' : '',
                                    isTaken ? 'taken' : '',
                                    isPicked ? 'picked' : '',
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                disabled={isTaken || busy}
                                onClick={() => highlight(n)}
                            >
                                {n}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="board-actions w-board-actions">
                <button
                    type="button"
                    className="btn-refresh"
                    disabled={busy}
                    onClick={() => void load().catch((e) => setError(extractMessage(e)))}
                >
                    Refresh
                </button>
                <button
                    type="button"
                    className={`btn-select${middleBusy ? ' is-confirming' : ''}${middleDeselect ? ' is-deselect' : ''}`}
                    disabled={middleDisabled}
                    onClick={() => {
                        if (middleDeselect) {
                            deselect();
                            return;
                        }
                        void selectNumber();
                    }}
                >
                    {middleLabel}
                </button>
                <button type="button" className="btn-start" disabled={busy || mine.size === 0} onClick={startGame}>
                    Start Game
                </button>
            </div>
        </div>
    );
}

function extractMessage(error: unknown): string {
    if (typeof error === 'object' && error !== null && 'response' in error) {
        const res = (error as { response?: { data?: { message?: string } } }).response;
        if (res?.data?.message) return res.data.message;
    }
    return error instanceof Error ? error.message : 'Unknown error';
}
